import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    Badge,
    Button,
    Card,
    Col,
    Container,
    Form,
    Row,
    Spinner,
    Table,
    Pagination, Modal, Alert,
} from "react-bootstrap";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

import { t } from "i18next";

import DataManager from "../../../General/DataManager";
import UserClass from "../../../General/LocalStorage/UserClass";
import Functions from "../../../General/Functions";
import { exportTicketsCsv, exportTicketsPdf } from "./ExportService";

const BoardingTickets = () => {
    const navigate = useNavigate();
    const { routeId } = useParams();
    const [searchParams] = useSearchParams();
    const description = searchParams.get("description");
    const departureTime = searchParams.get("departureTime");
    const port = searchParams.get("port");

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(t("all"));
    const [channelFilter, setChannelFilter] = useState(t("all"));
    const [confirmation, setConfirmation] = useState({
        show: false,
        ticket: null,
        action: null,
    });
    const [selectedTickets, setSelectedTickets] = useState([]);
    const [bulkConfirmation, setBulkConfirmation] = useState({
        show: false,
        action: null,
    });

    //const limit = 10;
    const [limit, setLimit] = useState(10);

    const [page, setPage] = useState(1);



    useEffect(() => {
        setPage(1);
    }, [search, statusFilter, channelFilter, limit]);

    const openConfirmation = (ticket, status) => {
        setConfirmation({
            show: true,
            ticket,
            action: status === t("boarded") ? "checkout" : "checkin",
        });
    };

    const confirmAction = async () => {
        if (!confirmation.ticket) return;

        try {

            const token = UserClass.getInstance().getTokenUser();

            const operation = confirmation.action === "checkin" ? "checkin" : "checkout";

            const { msgError } = await DataManager.changeTicketBoardingStatus(token, operation, routeId, confirmation.ticket.departurePort, [confirmation?.ticket?.number]);

            if (msgError) {
                Functions.alertMsg(msgError);
                return;
            }

            const message =
                operation === "checkin"
                    ? t("checkin_completed_successfully")
                    : t("checkout_completed_successfully");
            Functions.alertMsgSuccess(
                message
            );

            setConfirmation({
                show: false,
                ticket: null,
                action: null,
            });

            // Refresh the list
            getTickets(search.length >= 3 ? search : "");

        } catch (error) {
            Functions.alertMsg(t("msgErrorGeneral"));
        }


        setConfirmation({
            show: false,
            ticket: null,
            action: null,
        });
    };

    const openBulkConfirmation = (action) => {

        if (selectedTickets.length === 0) {
            Functions.alertMsg(t("no_tickets_selected"));
            return;
        }

        setBulkConfirmation({
            show: true,
            action,
        });
    };

    const confirmBulkAction = async () => {

        if (selectedTickets.length === 0) return;

        try {

            const token = UserClass.getInstance().getTokenUser();

            const numbers = selectedTickets.map(
                ticket => ticket.number
            );

            const operation = bulkConfirmation.action;

            const departurePort =
                selectedTickets[0].departurePort;


            const { msgError } =
                await DataManager.changeTicketBoardingStatus(
                    token,
                    operation,
                    routeId,
                    departurePort,
                    numbers
                );


            if (msgError) {
                Functions.alertMsg(msgError);
                return;
            }


            Functions.alertMsgSuccess(
                operation === "checkin"
                    ? t("checkin_completed_successfully")
                    : t("checkout_completed_successfully")
            );


            setBulkConfirmation({
                show: false,
                action: null,
            });


            setSelectedTickets([]);


            // reload data from backend
            getTickets(search.length >= 3 ? search : "");


        } catch (error) {

            Functions.alertMsg(
                t("msgErrorGeneral")
            );

        }
    };

    const getTickets = useCallback(async (search) => {

        try {

            setLoading(true);

            const token = UserClass.getInstance().getTokenUser();

            const { data, msgError } =
                await DataManager.getRouteTickets(token, routeId, port, search || "");

            if (msgError) {
                Functions.alertMsg(msgError);
                return;
            }

            setTickets(data || []);

        } catch {
            Functions.alertMsg(t("msgErrorGeneral"));
        } finally {
            setLoading(false);
        }
    }, [routeId, port]);

    useEffect(() => {
        if (!routeId) return;
        const timeout = setTimeout(() => {
            getTickets(search.length >= 3 ? search : "");
        }, 500);

        return () => clearTimeout(timeout);
    }, [search, routeId, getTickets]);

    const getTicketStatus = (ticket) => {
        if (ticket.stato === "NoShow") return "No Show";
        if (ticket.stato === "boarded" || ticket.stato === "Imbarcato") return t('boarded');
        return "Pending";
    };

    const getBadgeVariant = (status) => {
        switch (status) {
            case t('boarded'):
                return "success";
            case "Checked In":
                return "primary";
            case "Pending":
                return "warning";
            case "No Show":
                return "danger";
            default:
                return "secondary";
        }
    };

    const stats = useMemo(() => ({

        totalTickets: tickets.length,
        passengers: tickets.reduce(
            (sum, t) => sum + (t.passengers?.total ?? 0), 0
        ),
        boardedPassengers: tickets.reduce(
            (sum, t) => sum + (t.passengers?.boarded ?? 0),
            0
        ),
        accessories: tickets.reduce(
            (sum, t) => sum + (t.accessories?.total ?? 0),
            0
        ),
        boardedAccessories: tickets.reduce(
            (sum, t) => sum + (t.accessories?.boarded ?? 0),
            0
        ),
        vehicles: tickets.reduce(
            (sum, t) => sum + (t.vehicles?.total ?? 0),
            0
        ),
        boardedVehicles: tickets.reduce(
            (sum, t) => sum + (t.vehicles?.boarded ?? 0),
            0
        ),
        totalGrand:
            tickets.reduce(
                (sum, t) =>
                    sum +
                    (t.passengers?.total ?? 0) +
                    (t.accessories?.total ?? 0) +
                    (t.vehicles?.total ?? 0),
                0
            ),

        boardedGrand:
            tickets.reduce(
                (sum, t) =>
                    sum +
                    (t.passengers?.boarded ?? 0) +
                    (t.accessories?.boarded ?? 0) +
                    (t.vehicles?.boarded ?? 0),
                0
            ),

        noShowGrand:
            tickets.reduce(
                (sum, t) =>
                    sum +
                    ((t.passengers?.total ?? 0) - (t.passengers?.boarded ?? 0)) +
                    ((t.accessories?.total ?? 0) - (t.accessories?.boarded ?? 0)) +
                    ((t.vehicles?.total ?? 0) - (t.vehicles?.boarded ?? 0)),
                0
            ),
        noShow: tickets.filter(t => t.stato === "NoShow").length,
    }), [tickets]);

    const filteredTickets = useMemo(() => {
        return tickets.filter(ticket => {
            const status = getTicketStatus(ticket);

            const matchesStatus =
                statusFilter === t("all") ||
                status === statusFilter;

            const matchesChannel =
                channelFilter === t("all") ||
                ticket.issueType === channelFilter;

            return matchesStatus && matchesChannel;

        });
    }, [tickets, statusFilter, channelFilter]);
    const totalResults = filteredTickets.length;

    const totalPages = Math.max(
        1,
        Math.ceil(totalResults / limit)
    );

    const paginatedTickets = useMemo(() => {
        const start = (page - 1) * limit;

        return filteredTickets.slice(start, start + limit);
    }, [filteredTickets, page, limit]);






    const StatCard = ({ title, color, grandTotal, pax, acc, vei }) => (
        <Col md={4}>
            <Card className="shadow-sm border-0 h-100">
                <Card.Body>
                    <div className={`text-${color} small fw-semibold text-center`}>
                        {title}
                    </div>

                    {/*<div className="text-center mt-3 mb-3">
                        <h4 className="fw-bold mb-0">{grandTotal}</h4>
                    </div>*/}

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Badge bg="secondary">PAX</Badge>
                        <span className="fw-bold fs-5">{pax}</span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <Badge bg="info">ACC</Badge>
                        <span className="fw-bold fs-5">{acc}</span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <Badge bg="warning" text="dark">VEI</Badge>
                        <span className="fw-bold fs-5">{vei}</span>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );


    const exportRows = filteredTickets.map(ticket => ({
        [t('owner')]: ticket?.holderName?.toString() ?? '-',
        [t("booking")]: ticket?.bookingReference?.toString() ?? '-',
        [t("ticket")]: ticket?.number?.toString() ?? '-',
        [t('departure_time')]: ticket?.departureTime
            ? new Date(ticket.departureTime).toLocaleString()
            : "-",
        [t('departure_port')]: ticket?.departurePort,
        [t('arrival_port')]: ticket?.destinationPort,
        [t('status')]: getTicketStatus(ticket),
        [t('channel')]: ticket?.issueType ? (ticket?.issueType === "ticketOffice" ? t("ticket_office") : ticket?.issueType) : "-",
        [t('totalPassengers')]: ticket.passengers?.total ?? 0,
        [t('tariff')]: ticket?.tariff ?? '-',
        [t('boardedPassengers')]: ticket.passengers?.boarded ?? 0,
        [t('totalAccessories')]: ticket.accessories?.total ?? 0,
        [t('totalBoardedAccessories')]: ticket.accessories?.boarded ?? 0,
        [t('totalVehicles')]: ticket.vehicles?.total ?? 0,
        [t('totalBoardedVehicles')]: ticket.vehicles?.boarded ?? 0,
    }));

    const getPaginationItems = () => {
        const items = [];
        const delta = 2;

        const start = Math.max(2, page - delta);
        const end = Math.min(totalPages - 1, page + delta);

        items.push(
            <Pagination.Item
                key={1}
                active={page === 1}
                onClick={() => setPage(1)}
            >
                1
            </Pagination.Item>
        );

        if (start > 2) {
            items.push(<Pagination.Ellipsis key="start" disabled />);
        }

        for (let i = start; i <= end; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={page === i}
                    onClick={() => setPage(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (end < totalPages - 1) {
            items.push(<Pagination.Ellipsis key="end" disabled />);
        }

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={page === totalPages}
                    onClick={() => setPage(totalPages)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handleCheckInOut = (ticket, status) => {
        const isBoarded = status === t("boarded");

        const confirmed = window.confirm(
            isBoarded
                ? t("confirm_checkout_ticket")
                : t("confirm_checkin_ticket")
        );

        if (!confirmed) return;

        navigate(
            `/pages/boardingTickets/checkin/${routeId}/${ticket.departurePort}/${ticket.number}`
        );
    };

    return (
        <Container fluid className="py-4">

            <div className="mb-4">
                <h3 className="fw-bold mb-1">{t('tickets')}</h3>

                <div className="text-muted">{description}</div>

                {departureTime && (
                    <small className="text-muted">
                        {t('departure_time')}: {new Date(departureTime).toLocaleString()}
                    </small>
                )}
            </div>

            <Row className="g-3 mb-4">
                <StatCard
                    title={t('total')}
                    color="secondary"
                    //grandTotal={stats.totalGrand}
                    pax={stats.passengers}
                    acc={stats.accessories}
                    vei={stats.vehicles}
                />

                <StatCard
                    title={t("boarded")}
                    color="success"
                    //grandTotal={stats.boardedGrand}
                    pax={stats.boardedPassengers}
                    acc={stats.boardedAccessories}
                    vei={stats.boardedVehicles}
                />

                <StatCard
                    title="No Show"
                    color="danger"
                    //grandTotal={stats.noShowGrand}
                    pax={stats.passengers - stats.boardedPassengers}
                    acc={stats.accessories - stats.boardedAccessories}
                    vei={stats.vehicles - stats.boardedVehicles}
                />
            </Row>

            <Card className="shadow-sm border-0">

                <Card.Body>

                    <Row className="g-3 mb-4">

                        <Col lg={6}>
                            <Form.Label>{t("search")}</Form.Label>
                            <Form.Control
                                placeholder={t("search_by_passenger_booking_ticket")}
                                value={search}
                                /*onChange={(e) => {
                                    const value = e.target.value;
                                    setSearch(value);
                                    handleSearch(value);
                                }}*/
                                onChange={(e) => setSearch(e.target.value)}

                            />
                        </Col>

                        <Col lg={3}>
                            <Form.Label>
                                {t("status")}
                            </Form.Label>
                            <Form.Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option>{t("all")}</option>
                                <option>{t('boarded')}</option>
                                <option>No Show</option>
                            </Form.Select>
                        </Col>
                        <Col lg={3}>
                            <Form.Label>
                                {t("channel")}
                            </Form.Label>

                            <Form.Select
                                value={channelFilter}
                                onChange={(e) => setChannelFilter(e.target.value)}
                            >
                                <option>{t("all")}</option>
                                <option value="ticketOffice">
                                    {t("ticket_office")}
                                </option>
                                <option value="online">
                                    {t("online")}
                                </option>
                            </Form.Select>
                        </Col>

                        <Row className="g-2 justify-content-end">

                            <Col xs="auto">
                                <Button
                                    variant="success"
                                    onClick={() =>
                                        exportTicketsCsv(
                                            exportRows,
                                            `${description}_${departureTime}`
                                        )
                                    }
                                >
                                    {t("export_csv")}
                                </Button>
                            </Col>

                            <Col xs="auto">
                                <Button
                                    variant="danger"
                                    onClick={() =>
                                        exportTicketsPdf({
                                            rows: exportRows,
                                            fileName: `${description}_${departureTime}`,
                                            title: t("tickets"),
                                            description,
                                            departureTime: new Date(departureTime).toLocaleString(),
                                        })
                                    }
                                >
                                    {t("export_pdf")}
                                </Button>
                            </Col>

                        </Row>

                    </Row>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                        </div>
                    ) : filteredTickets.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            {t('no_tickets_found')}
                        </div>
                    ) : (

                        <>
                            {selectedTickets.length > 0 && (
                                <Card className="mb-3 border-primary">
                                    <Card.Body className="d-flex justify-content-between align-items-center">

                                        <span>
                                            {selectedTickets.length} {t("tickets_selected")}
                                        </span>

                                        <div>
                                            <Button
                                                variant="success"
                                                onClick={() =>

                                                    openBulkConfirmation("checkin")
                                                }
                                            >
                                                {t("checkin_selected")}
                                            </Button>

                                            <Button
                                                variant="danger"
                                                className="ms-2"
                                                onClick={() =>
                                                     openBulkConfirmation("checkout")
                                                }
                                            >
                                                {t("checkout_selected")}
                                            </Button>
                                        </div>

                                    </Card.Body>
                                </Card>
                            )}
                            <div className="table-responsive">
                                <Table hover className="align-middle table-nowrap">


                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-center align-middle" style={{ minWidth: "44px", width: "44px", paddingLeft: "8px", paddingRight: "8px" }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    className="d-flex justify-content-center align-items-center m-0 p-0"
                                                    checked={
                                                        paginatedTickets.length > 0 &&
                                                        paginatedTickets.every(t =>
                                                            selectedTickets.some(
                                                                s => s.number === t.number
                                                            )
                                                        )
                                                    }
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedTickets(prev => [
                                                                ...prev,
                                                                ...paginatedTickets
                                                                    .filter(ticket =>
                                                                        !prev.some(
                                                                            s => s.number === ticket.number
                                                                        )
                                                                    )
                                                                    .map(ticket => ({
                                                                        number: ticket.number,
                                                                        departurePort: ticket.departurePort,
                                                                        holderName: ticket.holderName
                                                                    }))
                                                            ]);
                                                        } else {
                                                            setSelectedTickets(prev =>
                                                                prev.filter(
                                                                    s =>
                                                                        !paginatedTickets.some(
                                                                            t => t.number === s.number
                                                                        )
                                                                )
                                                            );
                                                        }
                                                    }}
                                                />
                                            </th>
                                            <th>{t('owner')}</th>
                                            <th>{t('booking')}</th>
                                            <th>{t('ticket')}</th>
                                            <th >{t('arrival_port_short')}</th>
                                            <th className="text-center">{t('type')}</th>
                                            <th className="text-center">{t('tariff')}</th>
                                            <th className="text-center">{t('total')}</th>
                                            <th className="text-center">{t('boarded')}</th>
                                            <th>{t('status')}</th>
                                            <th>{t('channel')}</th>
                                            <th className="text-center">{t('actions')}</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {paginatedTickets.map(ticket => {

                                            const status = getTicketStatus(ticket);

                                            return (
                                                <tr key={ticket.id}>

                                                    <td className="text-center align-middle" style={{ minWidth: "44px", width: "44px", paddingLeft: "8px", paddingRight: "8px" }} >
                                                        <Form.Check
                                                            type="checkbox"
                                                            className="d-flex justify-content-center align-items-center m-0 p-0"
                                                            checked={
                                                                selectedTickets.some(
                                                                    s => s.number === ticket.number
                                                                )
                                                            }
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedTickets(prev => [
                                                                        ...prev,
                                                                        {
                                                                            number: ticket.number,
                                                                            departurePort: ticket.departurePort,
                                                                            holderName: ticket.holderName
                                                                        }
                                                                    ]);
                                                                } else {
                                                                    setSelectedTickets(prev =>
                                                                        prev.filter(
                                                                            s => s.number !== ticket.number
                                                                        )
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </td>

                                                    <td>
                                                        <div className="fw-semibold">
                                                            {ticket?.holderName ?? '-'}
                                                        </div>
                                                    </td>

                                                    <td>
                                                        {ticket?.bookingReference ?? '-'}
                                                    </td>

                                                    <td className="fw-bold">
                                                        {ticket?.number ?? '-'}
                                                    </td>

                                                    <td>
                                                        {ticket?.destinationPort ?? '-'}
                                                    </td>

                                                    <td>
                                                        <div className="small text-center">

                                                            {ticket?.passengers && (
                                                                <div className="mb-1">
                                                                    <Badge bg="secondary">PAX</Badge>
                                                                </div>
                                                            )}


                                                            {ticket?.accessories && (
                                                                <div className="mb-1">
                                                                    <Badge bg="info">ACC</Badge>
                                                                </div>
                                                            )}


                                                            {ticket?.vehicles && (
                                                                <div>
                                                                    <Badge bg="warning" text="dark">
                                                                        VEI
                                                                    </Badge>
                                                                </div>
                                                            )}




                                                        </div>
                                                    </td>
                                                    <td>
                                                        {ticket?.tariff}
                                                    </td>

                                                    <td className="text-center">
                                                        <div>


                                                            {ticket?.passengers && (
                                                                <div>{ticket?.passengers?.total}</div>

                                                            )}


                                                            {ticket?.accessories && (
                                                                <div>{ticket?.accessories?.total}</div>

                                                            )}


                                                            {ticket?.vehicles && (
                                                                <div>{ticket?.vehicles?.total}</div>

                                                            )}
                                                        </div>
                                                    </td>

                                                    <td className="text-center">
                                                        <div>


                                                            {ticket?.passengers && (
                                                                <div>{ticket?.passengers?.boarded}</div>

                                                            )}


                                                            {ticket?.accessories && (
                                                                <div>{ticket?.accessories?.boarded}</div>

                                                            )}


                                                            {ticket?.vehicles && (
                                                                <div>{ticket?.vehicles?.boarded}</div>

                                                            )}
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <Badge
                                                            bg={getBadgeVariant(status)}
                                                            text={status === "Pending" ? "dark" : undefined}
                                                        >
                                                            {status}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        {ticket?.issueType ?
                                                            (ticket?.issueType === "ticketOffice"
                                                                ? t("ticket_office")
                                                                : ticket?.issueType)
                                                            : "-"}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-wrap justify-content-center gap-2">

                                                            <Button
                                                                size="sm"
                                                                variant="outline-secondary"
                                                                className="d-flex align-items-center gap-1"
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/pages/boardingTickets/details/${routeId}/${ticket.departurePort}/${ticket.number}`
                                                                    )
                                                                }
                                                            >
                                                                <i className="bi bi-eye"></i>
                                                                {t("details")}
                                                            </Button>


                                                            <Button
                                                                size="sm"
                                                                variant={status === t("boarded") ? "danger" : "success"}
                                                                onClick={() => openConfirmation(ticket, status)}
                                                            >
                                                                <i className="bi bi-check-circle me-1"></i>

                                                                {status === t("boarded")
                                                                    ? t("checkout")
                                                                    : t("checkin")}
                                                            </Button>

                                                        </div>
                                                    </td>

                                                </tr>
                                            );
                                        })}
                                    </tbody>

                                </Table></div>


                            {totalResults > 0 && (
                                <div className="mt-4">
                                    <div
                                        className="
            d-flex
            flex-wrap
            align-items-center
            justify-content-between
            gap-3
        "
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <small className="text-muted text-nowrap">
                                                {t("rows_per_page")}
                                            </small>

                                            <Form.Select
                                                size="sm"
                                                value={limit}
                                                onChange={(e) => setLimit(Number(e.target.value))}
                                                style={{ width: 75 }}
                                            >
                                                {[10, 25, 50, 100].map(size => (
                                                    <option key={size} value={size}>
                                                        {size}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </div>

                                        <div className="overflow-auto">

                                            <Pagination size="sm" className="mb-0">
                                                <Pagination.Prev
                                                    disabled={page === 1}
                                                    onClick={() => setPage(page - 1)}
                                                />

                                                {getPaginationItems()}

                                                <Pagination.Next
                                                    disabled={page === totalPages}
                                                    onClick={() => setPage(page + 1)}
                                                />
                                            </Pagination>
                                        </div>

                                        <small className="text-muted text-nowrap">
                                            {(page - 1) * limit + 1}&ndash;
                                            {Math.min(page * limit, totalResults)} {t("of")} {totalResults}
                                        </small>
                                    </div>
                                </div>
                            )}</>



                    )}

                </Card.Body>

            </Card>

            <Modal
                show={confirmation.show}
                onHide={() =>
                    setConfirmation({
                        show: false,
                        ticket: null,
                        action: null,
                    })
                }
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {confirmation.action === "checkin"
                            ? t("confirm_checkin_title")
                            : t("confirm_checkout_title")}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>

                    <Alert
                        variant={
                            confirmation.action === "checkin"
                                ? "info"
                                : "warning"
                        }
                    >
                        {confirmation.action === "checkin"
                            ? t("checkin_warning")
                            : t("checkout_warning")}
                    </Alert>

                    {confirmation.ticket && (
                        <>
                            <Table bordered size="sm">
                                <tbody>
                                    <tr>
                                        <th>{t("owner")}</th>
                                        <td>{confirmation.ticket.holderName}</td>
                                    </tr>

                                    <tr>
                                        <th>{t("booking")}</th>
                                        <td>{confirmation.ticket.bookingReference}</td>
                                    </tr>

                                    <tr>
                                        <th>{t("ticket")}</th>
                                        <td>{confirmation.ticket.number}</td>
                                    </tr>

                                    <tr>
                                        <th>{t("destination")}</th>
                                        <td>{confirmation.ticket.destinationPort}</td>
                                    </tr>

                                    <tr>
                                        <th>PAX</th>
                                        <td>{confirmation.ticket.passengers?.total ?? 0}</td>
                                    </tr>

                                    <tr>
                                        <th>ACC</th>
                                        <td>{confirmation.ticket.accessories?.total ?? 0}</td>
                                    </tr>

                                    <tr>
                                        <th>VEI</th>
                                        <td>{confirmation.ticket.vehicles?.total ?? 0}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </>
                    )}

                </Modal.Body>

                <Modal.Footer>

                    <Button
                        variant="secondary"
                        onClick={() =>
                            setConfirmation({
                                show: false,
                                ticket: null,
                                action: null,
                            })
                        }
                    >
                        {t("cancel")}
                    </Button>

                    <Button
                        variant={
                            confirmation.action === "checkin"
                                ? "success"
                                : "danger"
                        }
                        onClick={confirmAction}
                    >
                        {confirmation.action === "checkin"
                            ? t("checkin")
                            : t("checkout")}
                    </Button>

                </Modal.Footer>
            </Modal>

            <Modal
                show={bulkConfirmation.show}
                centered
                onHide={() =>
                    setBulkConfirmation({
                        show: false,
                        action: null,
                    })
                }
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {bulkConfirmation.action === "checkin"
                            ? t("confirm_checkin_title")
                            : t("confirm_checkout_title")}
                    </Modal.Title>
                </Modal.Header>


                <Modal.Body>

                    <Alert
                        variant={
                            bulkConfirmation.action === "checkin"
                                ? "info"
                                : "warning"
                        }
                    >
                        {bulkConfirmation.action === "checkin"
                            ? t("checkin_warning")
                            : t("checkout_warning")}
                    </Alert>


                    <div className="text-center">

                        <h4 className="fw-bold">
                            {selectedTickets.length}
                        </h4>

                        <span>
                            {t("tickets_selected")}
                        </span>

                    </div>

                </Modal.Body>


                <Modal.Footer>

                    <Button
                        variant="secondary"
                        onClick={() =>
                            setBulkConfirmation({
                                show: false,
                                action: null
                            })
                        }
                    >
                        {t("cancel")}
                    </Button>


                    <Button
                        variant={
                            bulkConfirmation.action === "checkin"
                                ? "success"
                                : "danger"
                        }
                        onClick={confirmBulkAction}
                    >
                        {bulkConfirmation.action === "checkin"
                            ? t("checkin")
                            : t("checkout")}
                    </Button>

                </Modal.Footer>

            </Modal>

        </Container>
    );
};

export default BoardingTickets;